import { IBaseResource } from "../api.types";
import { IClauseMinimal } from "../clauses";
import type {
    IOrganizationGroupMinimal,
    ITenantOrganizationGroupMinimal,
} from "../organization-groups/organization-groups.types";
import { IOrganizationMinimal } from "../organizations";
import { ITopicMinimal } from "../topics";

export interface IClauseChecklistBase {
    name: string;
    description: string;
    model_type: AIModelType;
    show_gaps?: boolean;
}

export interface IClauseChecklistCreate
    extends
        IClauseChecklistBase,
        IClauseChecklistOrganizationsCreate,
        IClauseChecklistClausesCreate {
    organization_group_id?: number | null;
}

export interface IClauseChecklistUpdate extends IClauseChecklistBase {
    organization_ids?: number[];
    organization_group_id?: number | null;
}

export interface IClauseChecklist extends IClauseChecklistBase, IBaseResource {
    organizations: IOrganizationMinimal[];
    organization_group:
        | IOrganizationGroupMinimal
        | ITenantOrganizationGroupMinimal
        | null;
    show_gaps: boolean;
}

export interface IClauseChecklistRelevanceStatus {
    status: RelevanceJobStatus;
}

export interface IOrganizationClauseChecklist extends IBaseResource {
    name: string;
    description: string;
    is_directly_assigned: boolean;
    is_shared_via_group: boolean;
    clauses: IClauseMinimal[];
    topics: ITopicMinimal[];
    organization_group:
        | IOrganizationGroupMinimal
        | ITenantOrganizationGroupMinimal
        | null;
}

export interface IClauseChecklistList extends IClauseChecklist {
    organization_sort_name: string | null;
}

export interface IClauseChecklistMinimal {
    name: string;
}

export interface IClauseChecklistOrganizationsCreate {
    organization_ids: number[];
}

export interface IClauseChecklistClausesCreate {
    clause_ids: number[];
}

export interface IClauseChecklistOrganization extends IBaseResource {
    clause_checklist_id: number;
    organization_id: number;
}

export interface IClauseChecklistClause extends IBaseResource {
    clause_checklist_id: number;
    clause_id: number;
}

export enum AIModelType {
    CHATGPT = "ChatGPT",
    GEMINI = "Gemini",
}

export enum RelevanceJobStatus {
    NONE = "none",
    PENDING = "pending",
    PROCESSING = "processing",
    COMPLETED = "completed",
    FAILED = "failed",
}

export interface IClauseChecklistImportRequest {
    name: string;
    description: string;
    organization_id: number;
    object_name: string;
}

export interface ISkippedClauseRow {
    row_number: number;
    reason: string;
}

export interface IClauseChecklistImportPreprocessResponse {
    // Topics
    total_topics: number;
    invalid_topic_names: string[];

    // Enabled Clauses
    enabled_clause_rows: number;
    skipped_clause_rows: ISkippedClauseRow[];
}

export interface IClauseChecklistDuplicate {
    name: string;
    organization_id: number;
}

// --- Clause Checklist Topic Types ---

/**
 * Represents an enabled clause-section combination.
 * - topic_clause_id: The TopicClause junction ID (validates clause belongs to topic)
 * - document_section_type_id: Which section (null for "General"/unassigned section)
 */
export interface IEnabledClauseSection {
    topic_clause_id: number;
    document_section_type_id: number | null;
}

export interface IClauseChecklistTopicCreate {
    topic_id: number;
    enabled_clauses?: IEnabledClauseSection[];
}

// --- Topic with Enabled Clauses (for clause management) ---

/**
 * Topic for checklist context with enabled clause-section info.
 * - clause_count: Total number of clauses in the topic
 * - enabled_clauses: List of enabled clause-sections (empty = all enabled)
 */
export interface ITopicWithEnabledClauses {
    id: number;
    name: string;
    description?: string;
    clause_count: number;
    enabled_clauses: IEnabledClauseSection[];
    created_at: string;
    updated_at: string;
}

/**
 * Update enabled clause-sections for a single topic.
 */
export interface ITopicClausesUpdate {
    topic_id: number;
    enabled_clauses: IEnabledClauseSection[];
}

/**
 * Request body for batch updating enabled clause-sections across multiple topics.
 */
export interface IClauseChecklistTopicClausesUpdate {
    updates: ITopicClausesUpdate[];
}

export interface IClauseChecklistTopic extends IBaseResource {
    clause_checklist_id: number;
    topic_id: number;
    topic: ITopicMinimal;
}
